import { isSameDay, format } from 'date-fns'
import { useCallback, useState } from 'react'
import { useContentDragContext } from '../WeekView/ContentDragContext'
import { isWithinStoryDateRange } from '../WeekView/helpers'
import { getMomentDragData } from '../WeekView/MomentCard/dragData'
import {
  useContentDropProposal,
  type ContentReschedulePayload,
} from '../WeekView/useContentDropProposal'

type DraggedMoment = {
  id: string
  scheduledAt: string | Date
}

type UpdateMomentArgs = {
  momentId: string
  momentOriginalDate: string
  update: { scheduled_at: string | Date }
}

type UseHistogramDayDropHandlersArgs = {
  date: Date
  story?: unknown
  draggedMoment: DraggedMoment | null | undefined
  onRescheduleContent?: (payload: ContentReschedulePayload) => void
  onUpdateMoment?: (args: UpdateMomentArgs) => void
  /**
   * Week-timescale day cells format the update value as a string; the
   * Day-timescale cells pass the Date directly. Defaults to passing the
   * Date through unchanged.
   */
  formatUpdateDate?: (date: Date) => string | Date
  /** Activate the drop target on dragOver in addition to dragEnter. */
  activateOnDragOver?: boolean
}

type UseHistogramDayDropHandlersResult = {
  isActiveDropTarget: boolean
  isForbiddenDropTarget: boolean
  /** True when forbidden specifically because of a moment drag. Drives
   * the moment-only `DropWarner` overlay; content forbidden state has a
   * separate visual treatment on the column header. */
  isForbiddenMomentDrop: boolean
  isNotWithinStory: boolean
  isSameDateAsDragged: boolean
  onDragEnter: () => void
  onDragLeave: () => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
}

/**
 * Shared drag-drop wiring for histogram day cells. Both the Day-timescale
 * (`Histogram/Day`) and Week-timescale (`Histogram/Week/Day`) variants
 * support content-reschedule drops and moment drops; this hook owns the
 * active/forbidden state plus the four DnD callbacks so each variant only
 * deals with its own layout.
 */
export const useHistogramDayDropHandlers = ({
  date,
  story,
  draggedMoment,
  onRescheduleContent,
  onUpdateMoment,
  formatUpdateDate,
  activateOnDragOver = false,
}: UseHistogramDayDropHandlersArgs): UseHistogramDayDropHandlersResult => {
  const [isActiveDropTarget, setIsActiveDropTarget] = useState(false)
  const { draggedContent, setDraggedContent } = useContentDragContext()
  const { forbiddenReason, buildReschedulePayload } =
    useContentDropProposal(date)

  const isNotWithinStory = !isWithinStoryDateRange(date, story)
  const isSameDateAsDragged =
    draggedMoment != null && isSameDay(date, draggedMoment.scheduledAt)

  const onDragEnter = useCallback(() => {
    if (draggedContent) {
      setIsActiveDropTarget(true)
      return
    }
    if (!draggedMoment) return
    setIsActiveDropTarget((prev) => prev || true)
  }, [draggedContent, draggedMoment])

  const onDragLeave = useCallback(() => {
    setIsActiveDropTarget((prev) => (prev ? false : prev))
  }, [])

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      if (draggedContent && forbiddenReason) return
      e.preventDefault()
      if (activateOnDragOver) setIsActiveDropTarget(true)
    },
    [draggedContent, forbiddenReason, activateOnDragOver]
  )

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsActiveDropTarget(false)

    if (draggedContent) {
      const payload = buildReschedulePayload()
      if (!payload) return
      onRescheduleContent?.(payload)
      setDraggedContent(null)
      return
    }

    if (isNotWithinStory || isSameDateAsDragged) return

    const dragData = getMomentDragData(e)
    if (!dragData) return
    const scheduledAtForUpdate = formatUpdateDate
      ? formatUpdateDate(date)
      : date
    onUpdateMoment?.({
      momentId: dragData.id,
      momentOriginalDate: format(dragData.scheduledAt, 'yyyy-MM-dd'),
      update: { scheduled_at: scheduledAtForUpdate },
    })
  }

  const isForbiddenMomentDrop =
    isActiveDropTarget &&
    !draggedContent &&
    (isNotWithinStory || isSameDateAsDragged)
  const isForbiddenContentDrop =
    isActiveDropTarget && Boolean(draggedContent) && Boolean(forbiddenReason)
  const isForbiddenDropTarget = isForbiddenMomentDrop || isForbiddenContentDrop

  return {
    isActiveDropTarget,
    isForbiddenDropTarget,
    isForbiddenMomentDrop,
    isNotWithinStory,
    isSameDateAsDragged,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
  }
}
